<template>
    <div id="project" v-if="project">
        <div id="project-detail" :style="`background-color: ${Theme().backgroundColor}`">
            <div id="feature" :class="Classes().projectDetailBackground">
                <img id="feature-media-overlay" :class="['p-desktop', Classes().featureMediaOverlayPosition]" src="@/assets/Images/Projects/FeatureMediaOverlay.png" alt="Feature Media Overlay" title="Feature Media Overlay" v-if="showFeatureFadeOverlay"/>

                <!-- Feature - Landscape view -->
                <div id="feature-display-landscape" class="p-desktop" v-if="mediaDisplayType === 'Landscape'">
                    <Media
                        v-if="project.ProjectMedia"
                        :projectID="project.ProjectID"
                        :name="project.Name"
                        :media="Media('Feature')"
                        @media-display-type="SetMediaDisplayTypeProperties($event)"
                    />
                    <FeatureDetails
                        v-if="mediaDisplayType !== '' && project.ProjectMedia"
                        :project="project"
                        :mediaDisplayType="mediaDisplayType"
                        :theme="Theme()"
                        :position="Media('Feature')[0].DetailPosition"
                        :timelineProjectMapID="timelineProjectMapID"
                    />
                </div>
                <div id="feature-display-landscape" class="m-feature-display-landscape p-mobile" v-if="mediaDisplayType === 'Landscape'">
                    <FeatureDetails
                        v-if="mediaDisplayType !== '' && project.ProjectMedia"
                        :project="project"
                        :mediaDisplayType="mediaDisplayType"
                        :theme="Theme()"
                        :position="Media('Feature')[0].DetailPosition"
                        :timelineProjectMapID="timelineProjectMapID"
                    />
                    <Media
                        v-if="project.ProjectMedia"
                        :projectID="project.ProjectID"
                        :name="project.Name"
                        :media="Media('Feature')"
                        @media-display-type="SetMediaDisplayTypeProperties($event)"
                    />
                </div>

                <!-- Feature - Isolated view -->
                <div id="feature-display-isolated" class="flex m-pad m-row" :style="`height: ${FeatureDisplayHeight()}`" v-else> <!-- Isolated -->
                    <div id="feature-details" class="m-span no-margin-t">
                        <FeatureDetails
                            v-if="mediaDisplayType !== '' && project.ProjectMedia"
                            :project="project"
                            :mediaDisplayType="mediaDisplayType"
                            :theme="Theme()"
                            :position="Media('Feature')[0].DetailPosition"
                            :timelineProjectMapID="timelineProjectMapID"
                        />
                    </div>
                    <div class="flex-right no-margin-t">
                        <Media
                            v-if="project.ProjectMedia"
                            :projectID="project.ProjectID"
                            :name="project.Name"
                            :media="Media('Feature')"
                            @media-display-type="SetMediaDisplayTypeProperties($event)"
                        />
                    </div>
                </div>

            </div>

            <!-- Reach consists of Impact & Cause -->
            <Reach
                v-if="project.ProjectImpact"
                :impact="Impact()"
                :cause="project.Cause"
                :mobile="mobile"
            />

            <!-- Mobile-responsive -->
            <div id="project-detail-content" class="p-desktop">
                <div id="detail-buttons" class="flex-center m-row">
                    <DetailButton
                        v-for="(button, b) in ProjectDetailButtons"
                        :key="b"
                        :index="b"
                        :button="button"
                        :active="selectedDetail.selection === button.Name"
                        :mobile="false"
                        @select="$emit('select-detail-button', $event)"
                    />
                </div>
                <DetailContent
                    v-for="(content, c) in ProjectDetailButtons"
                    :key="c"
                    :show="selectedDetail.selection === content.Name"
                    :content="content.Name"
                    :project="project"
                    :initiated="selectedDetail.contentInitiated[content.Name]"
                    @content-initiated="$emit('content-initiated', $event)"
                />
            </div>
            <div id="project-detail-content" class="p-mobile">
                <div id="detail-buttons" class="flex-center m-row">
                    <DetailButton
                        v-for="(button, b) in ProjectDetailButtons"
                        :key="b"
                        :index="b"
                        :button="button"
                        :active="selectedDetail.selection === button.Name"
                        :mobile="true"
                        @select="$emit('select-detail-button', $event)"
                    >
                        <DetailContent
                            :key="b"
                            :show="selectedDetail.selection === ProjectDetailButtons[b].Name"
                            :content="ProjectDetailButtons[b].Name"
                            :project="project"
                            :initiated="selectedDetail.contentInitiated[ProjectDetailButtons[b].Name]"
                            :mobile="true"
                            @content-initiated="$emit('content-initiated', $event)"
                        />
                    </DetailButton>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import Reach from "@/components/Projects/Project/Reach/Reach.vue";
import Media from "@/components/Projects/Project/Feature/Media.vue";
import DetailButton from "@/components/Projects/Project/Detail/Button.vue";
import DetailContent from "@/components/Projects/Project/Detail/Content.vue";
import FeatureDetails from "@/components/Projects/Project/Feature/Details.vue";
import { CONFIG } from "@/assets/JS/Config.js";
import ProjectDetailButtons from "@/assets/JS/Data/ProjectDetailButtons.json";
import { projectModel } from "@/models";

export default {
    name: "Project",
    props: {
        project: Object,
        selectedDetail: Object,
        timelineProjectMapID: Number,
        mobile: Boolean
    },
    components: {
        Reach,
        Media,
        DetailButton,
        DetailContent,
        FeatureDetails
    },
    data: () => ({
        ProjectDetailButtons,
        CONFIG,
        showFeatureFadeOverlay: false,
        mediaDisplayType: "",
        feature: {}
    }),
    methods: {
        Theme: function() {
            const palette = this.Palette();
            if (palette) {
                const {
                    ProjectTitle,
                    ClientYear,
                    FeatureBackground,
                    Copy,
                    Primary
                } = palette;
                return {
                    projectTitle: ProjectTitle,
                    clientYear: ClientYear,
                    backgroundColor: FeatureBackground,
                    copy: Copy,
                    primary: Primary
                };
            }
            return CONFIG.projects.palette;
        },
        Media: function(type) {
            const media = [];
            for (const projectMedia of this.project.ProjectMedia) {
                const { Type, ProjectID, TimelineProjectMapID } = projectMedia;
                if (Type === type && ProjectID === this.project.ProjectID && TimelineProjectMapID === this.timelineProjectMapID) {
                    media.push(projectMedia);
                }
            }
            this.feature = media[0];
            return media;
        },
        Classes: function() {
            const palette = this.Palette();
            let projectDetailBackground = "";
            if (palette) {
                const { FeatureBackground } = palette;
                if (!FeatureBackground) {
                    projectDetailBackground = "layered-project-background";
                }
                if (FeatureBackground === "Layered Light") {
                    projectDetailBackground = "layered-project-background-light";
                } else if (FeatureBackground === "Layered Dark") {
                    projectDetailBackground = "layered-project-background-dark";
                }
            }

            // Feature media overlay
            let featureMediaOverlayPosition = "";
            const { DetailPosition } = this.feature;
            if (DetailPosition) {
                featureMediaOverlayPosition = `feature-media-overlay-${DetailPosition.toLowerCase()}`;
            }

            return {
                projectDetailBackground,
                featureMediaOverlayPosition
            };
        },
        Impact: function() {
            let impact = {};
            for (const projectImpact of this.project.ProjectImpact) {
                if (projectImpact.TimelineProjectMapID === this.timelineProjectMapID) {
                    impact = projectImpact;
                }
            }
            return impact;
        },
        SetMediaDisplayTypeProperties: function(mediaDisplayType) {
            this.mediaDisplayType = mediaDisplayType;
            let showFeatureFadeOverlay = false;
            if (mediaDisplayType === "Landscape" && this.feature.ShowFeatureOverlay) {
                showFeatureFadeOverlay = true;
            }
            if (mediaDisplayType === "Isolated") {
                showFeatureFadeOverlay = false;
            }
            this.showFeatureFadeOverlay = showFeatureFadeOverlay;
        },
        Palette: function() {
            if (this.project.ProjectPalette) {
                return this.project.ProjectPalette.filter(p => p.TimelineProjectMapID === this.timelineProjectMapID)[0];
            }
        },
        FeatureDisplayHeight: function() {
            return !this.mobile ? `${CONFIG.projects.detailViewportHeight}px` : "auto";
        }
    },
    beforeMount: function() {
        for (const detailButton of ProjectDetailButtons) {
            this.selectedDetail.contentInitiated[detailButton.Name] = false;
        }
    },
    emits: [
        ...projectModel.emits,
        "select-detail-button"
    ]
};
</script>